import { useLocalStorage, useNow } from '@vueuse/core'
import { DateTime } from 'luxon'
import { computed } from 'vue'
import { useGetMe } from '@/composables/get-me'

const STORAGE_KEY = 'flash-sale-modal-dismissed-at'
const SALE_START = DateTime.fromISO('2026-05-27T00:00:00.000Z', { zone: 'utc' })
const SALE_END = DateTime.fromISO('2026-06-04T16:00:00.000Z', { zone: 'utc' }).plus({ hours: 2 })

export const useFlashSaleModal = () => {
  const now = useNow({ interval: 1_000 })
  const dismissedByAccount = useLocalStorage<Record<string, string>>(STORAGE_KEY, {})
  const { accountAddress, isConnected } = useGetMe()

  const todayKey = computed(() => {
    return DateTime.fromJSDate(now.value).toISODate() ?? ''
  })

  const isDismissedToday = computed(() => {
    if (!accountAddress.value) return false
    return dismissedByAccount.value[accountAddress.value] === todayKey.value
  })

  const isSaleActive = computed(() => {
    const current = DateTime.fromJSDate(now.value)
    return current >= SALE_START && current < SALE_END
  })

  const timeRemaining = computed(() => {
    const current = DateTime.fromJSDate(now.value)
    const diff = SALE_END.diff(current, ['days', 'hours', 'minutes', 'seconds']).toObject()
    return {
      days: Math.max(0, Math.floor(diff.days ?? 0)),
      hours: Math.max(0, Math.floor(diff.hours ?? 0)),
      minutes: Math.max(0, Math.floor(diff.minutes ?? 0)),
      seconds: Math.max(0, Math.floor(diff.seconds ?? 0)),
    }
  })

  const shouldShowModal = computed(() => {
    return Boolean(
      isConnected.value &&
      isSaleActive.value &&
      !isDismissedToday.value,
    )
  })

  const dismissForToday = () => {
    if (!accountAddress.value || !todayKey.value) return
    dismissedByAccount.value = {
      ...dismissedByAccount.value,
      [accountAddress.value]: todayKey.value,
    }
  }

  return {
    isSaleActive,
    timeRemaining,
    shouldShowModal,
    dismissForToday,
  }
}
